import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Metric,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@tremor/react';
import { getFeedback, extractErrorMessage } from '@/lib/api';

interface QRow {
  question: string;
  sim: number;
  nao: number;
  pct_sim: number;
  total: number;
  resultado?: string;
  faixa_casos?: string;
  canal?: string;
  usuarios?: number;
}
interface Respondente {
  user_id: number;
  email: string;
  nome: string | null;
  canal: string | null;
  n_casos: number;
  ult_status: string | null;
  respostas: Record<string, string>;
}
interface InappThumb {
  processo_id: string;
  thumb: string;
  created_at: string | null;
  email: string | null;
  nome: string | null;
  status: string | null;
}
interface Inapp {
  sim: number;
  nao: number;
  total: number;
  usuarios: number;
  pct_sim: number;
  recentes: InappThumb[];
}
interface FeedbackData {
  enviados: number;
  responderam: number;
  total_respostas: number;
  taxa_resposta_pct: number;
  perguntas_label: Record<string, string>;
  por_pergunta: QRow[];
  por_resultado_caso: QRow[];
  por_n_casos: QRow[];
  por_canal: QRow[];
  respondentes: Respondente[];
  inapp?: Inapp;
}

const RESULTADO_LABEL: Record<string, string> = {
  '1_completo': 'Cálculo completo',
  '2_outro': 'Outro/sem-resultado',
  '3_erro': 'Deu erro',
};

function thumb(v?: string) {
  if (v === 'up') return <Badge color="emerald">👍</Badge>;
  if (v === 'down') return <Badge color="rose">👎</Badge>;
  return <Text>—</Text>;
}

function PctBar({ row, label }: { row: QRow; label: string }) {
  return (
    <Card>
      <Text>{label}</Text>
      <Metric>{row.total ? `${row.pct_sim}% 👍` : '—'}</Metric>
      <Text className="mt-1">
        {row.sim} sim · {row.nao} não · {row.total} resp.
      </Text>
    </Card>
  );
}

export function FeedbackPage() {
  const { data, isLoading, error } = useQuery<FeedbackData>({
    queryKey: ['feedback'],
    queryFn: () => getFeedback().then((r) => r.data),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Text>Carregando feedback…</Text>;
  if (error)
    return <Text>Erro: {extractErrorMessage(error, 'erro ao carregar feedback')}</Text>;
  if (!data) return <Text>Sem dados.</Text>;

  const lbl = data.perguntas_label || {};
  const byQ = (q: string) =>
    data.por_pergunta.find((r) => r.question === q) ||
    ({ question: q, sim: 0, nao: 0, pct_sim: 0, total: 0 } as QRow);

  return (
    <div className="space-y-6">
      <div>
        <Title>Feedback dos clientes (👍/👎)</Title>
        <Text>
          O cálculo da JusQuant está sendo valioso? Dois canais: o <b>thumb dentro do app</b>
          {' '}(na tela do caso) e a <b>pesquisa por e-mail</b> de satisfação.
        </Text>
      </div>

      {/* Thumbs IN-APP (na tela do caso) — canal separado do survey de e-mail */}
      {data.inapp && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <Title>👍/👎 dentro do app (na tela do caso)</Title>
              <Text>
                Votos dados no botão da tela do caso. Canal diferente da pesquisa por e-mail
                abaixo. Estes alimentam o índice de confiança e os loops de autocorreção.
              </Text>
            </div>
            <div className="text-right">
              <Metric>
                {data.inapp.total ? `${data.inapp.pct_sim}% 👍` : '—'}
              </Metric>
              <Text>
                {data.inapp.sim} 👍 · {data.inapp.nao} 👎 · {data.inapp.total} votos
              </Text>
            </div>
          </div>
          {data.inapp.recentes.length > 0 && (
            <Table className="mt-3">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Caso</TableHeaderCell>
                  <TableHeaderCell>Voto</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Usuário</TableHeaderCell>
                  <TableHeaderCell>Quando</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.inapp.recentes.map((r) => (
                  <TableRow key={r.processo_id}>
                    <TableCell>#{r.processo_id}</TableCell>
                    <TableCell>{thumb(r.thumb)}</TableCell>
                    <TableCell>{r.status || '—'}</TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>
                      {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      <Title className="mt-2">Pesquisa de satisfação por e-mail</Title>
      <Grid numItemsSm={2} numItemsLg={4} className="gap-4">
        <Card>
          <Text>Enviados</Text>
          <Metric>{data.enviados}</Metric>
        </Card>
        <Card>
          <Text>Responderam</Text>
          <Metric>{data.responderam}</Metric>
        </Card>
        <Card>
          <Text>Taxa de resposta</Text>
          <Metric>{data.taxa_resposta_pct}%</Metric>
        </Card>
        <Card>
          <Text>Total de respostas</Text>
          <Metric>{data.total_respostas}</Metric>
        </Card>
      </Grid>

      <div>
        <Title className="mb-2">Por pergunta</Title>
        <Grid numItemsSm={1} numItemsLg={3} className="gap-4">
          {['liked', 'accurate', 'helps_money'].map((q) => (
            <PctBar key={q} row={byQ(q)} label={lbl[q] || q} />
          ))}
        </Grid>
      </div>

      <Card>
        <Title>👍 por pergunta × RESULTADO do último caso</Title>
        <Text className="mb-3">
          O sinal-chave: quem teve cálculo bom acha mais valioso?
        </Text>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Pergunta</TableHeaderCell>
              <TableHeaderCell>Resultado do caso</TableHeaderCell>
              <TableHeaderCell>% 👍</TableHeaderCell>
              <TableHeaderCell>sim / não</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.por_resultado_caso.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{lbl[r.question] || r.question}</TableCell>
                <TableCell>{RESULTADO_LABEL[r.resultado || ''] || r.resultado}</TableCell>
                <TableCell>{r.total ? `${r.pct_sim}%` : '—'}</TableCell>
                <TableCell>
                  {r.sim} / {r.nao}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Grid numItemsLg={2} className="gap-4">
        <Card>
          <Title>Por engajamento (nº de casos)</Title>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Pergunta</TableHeaderCell>
                <TableHeaderCell>Casos</TableHeaderCell>
                <TableHeaderCell>% 👍</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.por_n_casos.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{lbl[r.question] || r.question}</TableCell>
                  <TableCell>{r.faixa_casos}</TableCell>
                  <TableCell>{r.total ? `${r.pct_sim}%` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Card>
          <Title>Por canal de aquisição</Title>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Canal</TableHeaderCell>
                <TableHeaderCell>Usuários</TableHeaderCell>
                <TableHeaderCell>% 👍</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.por_canal.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.canal}</TableCell>
                  <TableCell>{r.usuarios}</TableCell>
                  <TableCell>{r.total ? `${r.pct_sim}%` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Grid>

      <Card>
        <Title>Quem respondeu (drill-down)</Title>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Casos</TableHeaderCell>
              <TableHeaderCell>Último caso</TableHeaderCell>
              <TableHeaderCell>Gostou</TableHeaderCell>
              <TableHeaderCell>Preciso</TableHeaderCell>
              <TableHeaderCell>Ajuda $</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.respondentes.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.n_casos}</TableCell>
                <TableCell>{u.ult_status || '—'}</TableCell>
                <TableCell>{thumb(u.respostas?.liked)}</TableCell>
                <TableCell>{thumb(u.respostas?.accurate)}</TableCell>
                <TableCell>{thumb(u.respostas?.helps_money)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
